import { useMemo } from "react";
import type { GroupByMode } from "./groupProducts";
import { UNASSIGNED, type ProductFilters } from "./productFilters";
import type { MatrixSort } from "./stockHealth";
import { useUrlParam } from "./useUrlParam";
import {
  formatGroupBy,
  formatIdSet,
  formatQuery,
  formatSort,
  parseGroupBy,
  parseIdSet,
  parseQuery,
  parseSort,
} from "./overviewParams";

// The Inventory Overview toolbar's search, product groups, Group by, hidden
// warehouses and sort, each kept in the URL (?q=, ?groups=, ?group=, ?hide=,
// ?sort=) so a bookmark opens the same view. Product groups and warehouses
// not on the page are dropped from the link.
export function useOverviewParams(productGroupIds: string[], warehouseIds: string[]) {
  const [query, setQuery] = useUrlParam("q", parseQuery, formatQuery);
  const [groups, setGroups] = useUrlParam(
    "groups",
    (raw) => parseIdSet(raw, new Set([...productGroupIds, UNASSIGNED])),
    formatIdSet,
  );
  const [groupBy, setGroupBy] = useUrlParam<GroupByMode>("group", parseGroupBy, formatGroupBy);
  const [hiddenWarehouseIds, setHiddenWarehouseIds] = useUrlParam(
    "hide",
    (raw) => parseIdSet(raw, new Set(warehouseIds)),
    formatIdSet,
  );
  const [sort, setSort] = useUrlParam<MatrixSort>("sort", parseSort, formatSort);

  // Same object until the search or groups change, so the page's memos hold.
  const filters = useMemo<ProductFilters>(() => ({ query, groups }), [query, groups]);
  const setFilters = (next: ProductFilters) => {
    setQuery(next.query);
    setGroups(next.groups);
  };

  return { filters, setFilters, groupBy, setGroupBy, hiddenWarehouseIds, setHiddenWarehouseIds, sort, setSort };
}
